import React from 'react';
import Form from 'react-bootstrap/Form';
import {
  ariaDescribedByIds,
  enumOptionsDeselectValue,
  enumOptionsIsSelected,
  enumOptionsSelectValue,
  enumOptionsValueForIndex,
  optionId,
  FormContextType,
  RJSFSchema,
  StrictRJSFSchema,
  WidgetProps,
} from '@rjsf/utils';
import moment from 'moment';

function OldWizCheckboxesViewerWidget<T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>({
  id,
  disabled,
  options,
  value,
  autofocus,
  readonly,
  required,
  onChange,
  onBlur,
  onFocus,
}: WidgetProps<T, S, F>) {
  const { enumOptions, enumDisabled, inline, emptyValue } = options;
  const checkboxesValues = Array.isArray(value) ? value : [value];

  const _onChange =
    (index: number) =>
    ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
      if (checked) {
        onChange(enumOptionsSelectValue<S>(index, checkboxesValues, enumOptions));
      } else {
        onChange(enumOptionsDeselectValue<S>(index, checkboxesValues, enumOptions));
      }
    };

  const _onBlur = ({ target: { value } }: React.FocusEvent<HTMLInputElement>) =>
    onBlur(id, enumOptionsValueForIndex<S>(value, enumOptions, emptyValue));
  const _onFocus = ({ target: { value } }: React.FocusEvent<HTMLInputElement>) =>
    onFocus(id, enumOptionsValueForIndex<S>(value, enumOptions, emptyValue));

  return (
    <Form.Group>
      <ul>
        {Array.isArray(enumOptions) &&
          enumOptions.map((option, index: number) => {
            const checked = enumOptionsIsSelected<S>(option.value, checkboxesValues);
            const itemDisabled = Array.isArray(enumDisabled) && enumDisabled.indexOf(option.value) !== -1;

            return checked ? <li key={`form-group-${index}`}>{option.label}</li> : '';
          })}
      </ul>
    </Form.Group>
  );
}

function WizCheckboxesViewerWidget<T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>({
  id,
  disabled,
  options,
  value,
  autofocus,
  readonly,
  required,
  onChange,
  onBlur,
  onFocus,
  schema,
}: WidgetProps<T, S, F>) {
  const { enumOptions, enumDisabled, inline, emptyValue } = options;
  const checkboxesValues = Array.isArray(value) ? value : [value];

  // If the field value is an array, render the selected options as a list
  if (Array.isArray(value)) {
    return (
      <Form.Group>
        <ul>
          {Array.isArray(enumOptions) &&
            enumOptions.map((option, index: number) => {
              const checked = enumOptionsIsSelected<S>(option.value, checkboxesValues);
              return checked ? <li key={`form-group-${index}`}>{option.label}</li> : null;
            })}
        </ul>
      </Form.Group>
    );
  }

  // Otherwise (single boolean or non-array enum) render as a read-only checkbox showing the field title
  return (
    <Form.Group>
      <Form.Check
        type="checkbox"
        id={id}
        label={schema?.title}
        checked={!!value}
        disabled={false}
        readOnly={true}
        style={{
          pointerEvents: 'none',
          opacity: 1,
          cursor: 'default',
        }}
      />
    </Form.Group>
  );
}

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function formatDate(value: string): string | null {
  if (DATE_ONLY_RE.test(value)) {
    const m = moment(value, 'YYYY-MM-DD', true);
    return m.isValid() ? m.format('LL') : null;
  }
  if (ISO_DATETIME_RE.test(value)) {
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    const dateStr = d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const timeStr = d
      .toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      .replace(' ', '');
    const tzPart = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(d)
      .find((p) => p.type === 'timeZoneName');
    const tzStr = tzPart ? tzPart.value : '';
    return tzStr ? `${dateStr}, ${timeStr} (${tzStr})` : `${dateStr}, ${timeStr}`;
  }
  return null;
}

const InputViewerWidget: React.FC<WidgetProps> = (props) => {
  const { value, options } = props;
  if (typeof value === 'boolean' || typeof value === 'number') {
    const enumOptions = options?.enumOptions as
      | Array<{ value: any; label: string }>
      | undefined;
    const match = enumOptions?.find((opt) => opt.value === value);
    if (match) {
      return <p>{match.label}</p>;
    }
    if (typeof value === 'boolean') {
      return <p>{value ? 'Yes' : 'No'}</p>;
    }
    return <p>{value}</p>;
  } else if (typeof value === 'string') {
    const formatted = formatDate(value);
    return <p>{formatted ?? value}</p>;
  } else if (value instanceof Date) {
    return <p>{moment(value).format('LL')}</p>;
  }
  return <></>;
};

const TableViewerWidget: React.FC<WidgetProps> = (props) => {
  const { value, schema } = props;
  if (!Array.isArray(value) || value.length === 0) return <></>;
  const rows = value.filter(
    (r): r is Record<string, any> => r !== null && typeof r === 'object',
  );
  if (rows.length === 0) return <></>;
  // Prefer the schema's column order + custom titles (matches the fill-out widget
  // TableHtmlFormWidget); fall back to data keys if the schema isn't available.
  const items = (schema as any)?.items?.properties || {};
  const schemaCols = Object.keys(items);
  const columns =
    schemaCols.length > 0
      ? schemaCols
      : Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
  return (
    <table className="table table-sm table-bordered">
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c}>{items[c]?.title || c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {columns.map((c) => (
              <td key={c}>{String(row[c] ?? '')}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const PasswordViewerWidget: React.FC<WidgetProps> = (props) => {
  const { value } = props;

  return <p>********</p>;
};

const EmptyWidget: React.FC<WidgetProps> = (props) => {
  return <></>;
};

const BooleanViewerWidget: React.FC<WidgetProps> = (props) => {
  const { value } = props;

  return (<><p>hello</p>
    <Form.Check
      type="checkbox"
      checked={value}
      disabled={true}
      readOnly={true}
      style={{ pointerEvents: 'none' }}
    /></>
  );
};

export {
  WizCheckboxesViewerWidget,
  InputViewerWidget,
  PasswordViewerWidget,
  EmptyWidget,
  BooleanViewerWidget,
  TableViewerWidget,
};
